import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAdminSchools, type AdminSchool } from '@/hooks/useAdminSchools'

interface SelectedSchoolContextValue {
  schools: AdminSchool[]
  loading: boolean
  error: string | null
  selectedSchoolId: string
  setSelectedSchoolId: (id: string) => void
  refresh: () => void
}

const SelectedSchoolContext = createContext<SelectedSchoolContextValue | null>(null)

/**
 * Shared "which school am I currently managing" selection for the school-admin portal,
 * provided once in AppShell so it persists across Instructors/Students (and any future
 * school-scoped page) navigation within the same session — never across a sign-out, and
 * never written to any browser storage.
 *
 * The selection itself is pure UI state and is NEVER a security boundary: every query
 * that uses it remains subject to the existing RLS policies (is_school_admin_for() on the
 * server), so a manipulated selectedSchoolId can at most ask for a school the caller
 * doesn't administer — RLS returns zero rows, not another school's data. This context
 * exists only so the UI asks for the *correct* school, not so it's trusted to.
 *
 * Revoked/removed membership: useAdminSchools() re-fetches the caller's own active
 * OWNER/ADMIN rows (RLS-scoped to auth.uid()) on every mount and on refresh() — nothing
 * here is cached beyond the current render. If the previously selected school drops out
 * of a fresh schools list (membership revoked), the effect below clears the stale
 * selection rather than continuing to silently query it.
 */
export function SelectedSchoolProvider({ children }: { children: ReactNode }) {
  const { schools, loading, error, refresh } = useAdminSchools()
  const [selectedSchoolId, setSelectedSchoolId] = useState('')

  useEffect(() => {
    if (loading) return
    const stillValid = schools.some(s => s.schoolId === selectedSchoolId)
    if (stillValid) return
    // Auto-select only when exactly one school is available — the existing,
    // zero-extra-step single-school workflow. With zero or multiple schools, an
    // explicit choice (or the empty state) is required; never guess.
    setSelectedSchoolId(schools.length === 1 ? schools[0].schoolId : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools, loading])

  return (
    <SelectedSchoolContext.Provider value={{ schools, loading, error, selectedSchoolId, setSelectedSchoolId, refresh }}>
      {children}
    </SelectedSchoolContext.Provider>
  )
}

export function useSelectedSchool(): SelectedSchoolContextValue {
  const ctx = useContext(SelectedSchoolContext)
  if (!ctx) throw new Error('useSelectedSchool must be used inside <SelectedSchoolProvider>')
  return ctx
}
